/**
 * @fileoverview CATMS Crew PWA - API Client Service
 * @description Backend API communication layer with offline fallback
 */

import { saveOfflineBundle } from '../utils/offlineAuth';

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
import { isRecovering } from '../utils/netGate';

// 共有端末トークン（制作会社が発行）。共有端末の初期設定時に localStorage に保存する。
// 設定方法: 端末ブラウザで  ?offline_token=otk_xxx  付きURLを一度開くと保存される。
const OFFLINE_TOKEN_KEY = 'catms_offline_token';
(() => {
  try {
    const p = new URLSearchParams(window.location.search).get('offline_token');
    if (p) { localStorage.setItem(OFFLINE_TOKEN_KEY, p); }
  } catch { /* SSR等では無視 */ }
})();
function getOfflineToken(): string | null {
  // 会社APKにビルド時埋め込まれた会社トークンを既定値とし、端末個別設定(localStorage)があれば優先。
  try { return localStorage.getItem(OFFLINE_TOKEN_KEY) ?? (import.meta.env.VITE_OFFLINE_TOKEN as string | undefined) ?? null; }
  catch { return (import.meta.env.VITE_OFFLINE_TOKEN as string | undefined) ?? null; }
}

/** デバイス管理に登録された端末トークン（管理端末の識別。旧APK互換の打刻許可にも使う） */
function getDeviceToken(): string | null {
  try { return localStorage.getItem('catms_device_token'); } catch { return null; }
}

/**
 * この端末に固有のID（初回アクセス時に作って端末内に保存する）。
 * モバイル回線は接続ごとにIPが変わるため、IPだけでは「一度ログインできた端末」を
 * 判別できず、打ち間違いでログイン回数制限に引っかかってしまう。
 * 個人スマホには登録端末トークンが無いので、この値で端末を見分ける。
 * 会社共通の X-Offline-Token とは違い、端末ごとに別の値になる。
 */
const CLIENT_ID_KEY = 'catms_client_device_id';
function getClientDeviceId(): string | null {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch { return null; }
}

// ── アプリの種別（別アプリとして配布するための明示フラグ）─────────────────
// 個人用アプリ  : URLに ?app=personal（閲覧専用。打刻UIは出さない）
// 管理用アプリ  : URLに ?app=managed （フル操作。動作確認・代理の検証もこちらで行う）
// フラグ無し    : 従来どおり端末トークンの有無で自動判定（後方互換）
// 個人用ホスト  : <会社>.my.unison-sea.com で開いたら常に個人用。船員アプリ（<会社>.crew）とは
//                 オリジンが別なので、同じブラウザで両方開いても localStorage の種別が混ざらない
const APP_VARIANT_KEY = 'catms_app_variant';
(() => {
  try {
    const v = new URLSearchParams(window.location.search).get('app');
    if (v === 'personal' || v === 'managed') localStorage.setItem(APP_VARIANT_KEY, v);
  } catch { /* SSR等では無視 */ }
})();
/** 明示されたアプリ種別を返す（個人用ホスト→URLフラグ/localStorage→ビルド時env、無ければnull） */
export function getAppVariant(): 'personal' | 'managed' | null {
  if (isPersonalHost()) return 'personal';
  try {
    const v = localStorage.getItem(APP_VARIANT_KEY);
    if (v === 'personal' || v === 'managed') return v;
  } catch { /* ignore */ }
  const env = import.meta.env.VITE_APP_VARIANT as string | undefined;
  return env === 'personal' || env === 'managed' ? env : null;
}
/** アプリ種別を明示的に切替（管理者が個人用アプリから管理用へ戻る等）。呼び出し側でreload推奨。 */
export function setAppVariant(v: 'personal' | 'managed'): void {
  try { localStorage.setItem(APP_VARIANT_KEY, v); } catch { /* ignore */ }
}
/** アプリ種別を切り替えて開き直す。個人用ホスト（<会社>.my）から管理用へは船員アプリのホスト（<会社>.crew）へ移る */
export function switchAppVariant(v: 'personal' | 'managed'): void {
  if (v === 'managed' && isPersonalHost()) {
    const parts = window.location.hostname.split('.');
    parts[1] = 'crew';
    window.location.href = `${window.location.protocol}//${parts.join('.')}/?app=managed`;
    return;
  }
  setAppVariant(v);
  window.location.reload();
}
/** 管理者・代理ログイン中か（個人用アプリで管理用へ切替できる人の判定に使う） */
export function isPrivilegedViewer(): boolean {
  return isAdminUser() || !!getImpersonation();
}

/**
 * 個人スマホ（閲覧用端末）かどうか。
 * 管理端末は「共有端末トークン（APK埋め込み）」か「登録済みデバイストークン」を持つ。
 * どちらも無い端末＝個人スマホとして、リアルタイム打刻のUIを隠し、閲覧・連絡・報告・勤怠修正を提供する。
 * （サーバー側でも打刻APIは同じ判定で遮断されるため、UIはあくまで案内目的）
 */
export function isPersonalDevice(): boolean {
  // 別アプリとして配布した明示フラグを最優先（個人用アプリ / 管理用アプリ）
  const variant = getAppVariant();
  if (variant === 'personal') return true;   // 個人用アプリ = 常に閲覧モード
  if (variant === 'managed')  return false;  // 管理用アプリ = 常にフル操作（代理・動作確認もこちら）
  // フラグ無し（従来）: 管理者・代理ログインはフル、端末トークンが無ければ個人スマホ扱い
  if (isAdminUser() || getImpersonation()) return false;
  return !getOfflineToken() && !getDeviceToken();
}

/** ログイン中ユーザーが管理者ロールか（PCからの打刻・フル操作を許可する判定に使用） */
function isAdminUser(): boolean {
  try {
    const u = JSON.parse(localStorage.getItem('catms_user') ?? 'null');
    return ['shore_admin', 'system_admin'].includes(u?.user_role);
  } catch { return false; }
}

/** Access token kept in memory only (not localStorage) */
let accessToken: string | null = null;

// ── 代理ログイン（教育・デモ用）: 管理者が船員として操作するセッション ──────────
// 代理トークンは sessionStorage に保持（リロードでも維持。タブを閉じれば消える）。
// リフレッシュCookieを持たないため、リロード時は silentRefresh でなくこのトークンを使う。
const IMP_TOKEN_KEY = 'catms_imp_token';
const IMP_FLAG_KEY  = 'catms_impersonation';
try {
  const t = sessionStorage.getItem(IMP_TOKEN_KEY);
  if (t) accessToken = t; // ブート時に代理トークンを復元
} catch { /* SSR等では無視 */ }

/** 代理ログインを開始（トークンとフラグを保存し、以後この人として操作） */
export function startImpersonation(token: string, byName: string): void {
  accessToken = token;
  try {
    sessionStorage.setItem(IMP_TOKEN_KEY, token);
    localStorage.setItem(IMP_FLAG_KEY, JSON.stringify({ by: byName }));
  } catch { /* ignore */ }
}
/** 代理ログイン中か（バナー表示・打刻許可の判定に使用） */
export function getImpersonation(): { by: string } | null {
  try { return JSON.parse(localStorage.getItem(IMP_FLAG_KEY) ?? 'null'); } catch { return null; }
}
/** 代理ログイン終了（トークン・フラグを破棄） */
export function endImpersonation(): void {
  try { sessionStorage.removeItem(IMP_TOKEN_KEY); localStorage.removeItem(IMP_FLAG_KEY); } catch { /* ignore */ }
}
/** ブート時に代理トークンがあるか（useAuth が silentRefresh を回避するために使う） */
export function hasImpersonationToken(): boolean {
  try { return !!sessionStorage.getItem(IMP_TOKEN_KEY); } catch { return false; }
}

/** 管理者が船員として代理ログイン（教育・デモ用）。要 管理者ロール。 */
export async function impersonate(userId: string): Promise<{ access_token: string; impersonated_by: string; user: Record<string, unknown> }> {
  const res = await apiFetch<{ success: boolean; data: { access_token: string; impersonated_by: string; user: Record<string, unknown> } }>(
    '/auth/impersonate', { method: 'POST', body: JSON.stringify({ user_id: userId }) });
  return res.data;
}

/** Set access token in memory only */
export function setTokens(access: string, _refresh?: string): void {
  accessToken = access;
  // refresh token is managed via HttpOnly cookie — do not store in localStorage
}

/** Silent refresh via HttpOnly cookie; call on page load if user data exists */
/**
 * アクセストークンの再取得。
 * 「サーバーに断られた（ログインし直しが必要）」のか「サーバーに届かなかった（圏外）」のかを
 * 区別して返す。沖では通信できないのが普通なので、届かなかっただけでログアウト扱いにしない。
 */
export async function silentRefreshDetailed(): Promise<'ok' | 'unauthorized' | 'offline'> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!res.ok) return 'unauthorized';
    const data = await res.json() as { data: { access_token: string } };
    accessToken = data.data.access_token;
    return 'ok';
  } catch {
    // fetch が例外＝ネットワークに届いていない（サーバーの返事ではない）
    return 'offline';
  }
}

export async function silentRefresh(): Promise<boolean> {
  return (await silentRefreshDetailed()) === 'ok';
}

/** Clear access token; cookie is cleared by the server on logout */
export function clearTokens(): void {
  accessToken = null;
  localStorage.removeItem('catms_access_token');
  localStorage.removeItem('catms_refresh_token');
  localStorage.removeItem('catms_user');
}

/** No-op: kept for backward compat — tokens are no longer loaded from localStorage */
export function loadTokens(): void { /* access token is memory-only; use silentRefresh() */ }

/** Get current access token */
export function getAccessToken(): string | null { return accessToken; }

/** apiFetch の追加オプション */
export interface ApiFetchOpts {
  /**
   * 401（かつサイレントリフレッシュ失敗）時に /login へ強制リダイレクトしない。
   * バックグラウンドのポーリング（勤怠セッション確認・ハートビート・GPS）や
   * 任意のアルコール検査チケット取得など、ユーザー操作に紐づかない呼び出しで指定する。
   * 打刻フローの途中で意図せずログイン画面へ戻されるのを防ぐ。
   */
  noRedirect?: boolean;
}

/** Generic API fetch with auth header */
export async function apiFetch<T>(path: string, options: RequestInit = {}, { noRedirect = false }: ApiFetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '1',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const webSubdomain = getWebCompanySubdomain();
  if (webSubdomain) headers['X-Company-Subdomain'] = webSubdomain;
  const offlineTok = getOfflineToken();
  if (offlineTok) headers['X-Offline-Token'] = offlineTok;
  const deviceTok = getDeviceToken();
  const clientId = getClientDeviceId();
  if (clientId) headers['X-Client-Device-Id'] = clientId;
  if (deviceTok) headers['X-Device-Token'] = deviceTok; // 管理端末の識別（打刻許可の判定に使用）
  // FormData（ファイル添付）は boundary をブラウザに任せる
  if (options.body instanceof FormData) delete headers['Content-Type'];

  const sentWithToken = !!accessToken;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 401) {
    // 圏外ログイン（トークン無し）のまま通信が戻った直後は、トークンの取り直しが終わるまでの401。
    // ここでログアウトさせない（打刻は「再ログイン」扱いで端末に保存され、表示は保存分のまま）。
    if (!sentWithToken && isRecovering()) throw new Error('再ログインが必要です。（通信の回復中）');
    // リフレッシュトークン（HttpOnly cookie）で再取得
    const refreshed = await silentRefresh();
    if (refreshed && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      const retryRes = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
      if (retryRes.ok) return retryRes.json();
    }
    clearTokens();
    // noRedirect 指定時はリダイレクトせず、呼び出し側に判断を委ねる（打刻フロー中断防止）。
    if (!noRedirect) window.location.replace('/login');
    throw new Error('再ログインが必要です。');
  }

  if (!res.ok) {
    const errData = await res.json().catch(() => ({ error: { message: 'Network error' } }));
    throw new Error(errData.error?.message ?? `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 認証付きでバイナリ（PDF/PNG/Excel/Word 等）を取得して Blob を返す。
 * apiFetch と同じ認証・会社識別ヘッダを付けつつ、JSON でなく Blob を返す。
 * 証明書作成（/cert-forms 等）のプレビュー画像やダウンロードに使う。
 */
export async function apiFetchBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '1',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const sub = getWebCompanySubdomain();
  if (sub) headers['X-Company-Subdomain'] = sub;
  const offlineTok = getOfflineToken();
  if (offlineTok) headers['X-Offline-Token'] = offlineTok;
  const deviceTok = getDeviceToken();
  const clientId = getClientDeviceId();
  if (clientId) headers['X-Client-Device-Id'] = clientId;
  if (deviceTok) headers['X-Device-Token'] = deviceTok;

  const run = () => fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let res = await run();
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed && accessToken) { headers['Authorization'] = `Bearer ${accessToken}`; res = await run(); }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: `HTTP ${res.status}` } }));
    throw new Error(err.error?.message ?? `HTTP ${res.status}`);
  }
  return res.blob();
}

/**
 * 認証付きで multipart/form-data を POST し JSON を返す（ファイルアップロード用）。
 * Content-Type はブラウザが boundary 付きで自動設定するため指定しない。
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { 'ngrok-skip-browser-warning': '1' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const sub = getWebCompanySubdomain();
  if (sub) headers['X-Company-Subdomain'] = sub;
  const offlineTok = getOfflineToken();
  if (offlineTok) headers['X-Offline-Token'] = offlineTok;
  const deviceTok = getDeviceToken();
  const clientId = getClientDeviceId();
  if (clientId) headers['X-Client-Device-Id'] = clientId;
  if (deviceTok) headers['X-Device-Token'] = deviceTok;

  const run = () => fetch(`${API_BASE}${path}`, { method: 'POST', body: form, headers, credentials: 'include' });
  let res = await run();
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed && accessToken) { headers['Authorization'] = `Bearer ${accessToken}`; res = await run(); }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: `HTTP ${res.status}` } }));
    throw new Error(err.error?.message ?? err.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 認証付き画像を取得して Object URL を返す。
 * `<img src>` に JWT を ?token= で埋める方式はトークン失効/未取得時に復活しない不具合があるため、
 * Authorization ヘッダ＋401時サイレントリフレッシュで取得し、blob を Object URL 化する。
 * @param resource `/api` 以降のパス（例: 'avatars/xxx.jpg', 'vessel-images/yyy.jpg'）
 */
export async function fetchImageObjectURL(resource: string): Promise<string> {
  const run = () => {
    const headers: Record<string, string> = { 'ngrok-skip-browser-warning': '1' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const sub = getWebCompanySubdomain();
    if (sub) headers['X-Company-Subdomain'] = sub;
    return fetch(`${API_BASE}/${resource}`, { headers, credentials: 'include' });
  };
  let res: Response;
  try {
    res = await run();
    if (res.status === 401) {
      const refreshed = await silentRefresh();
      if (refreshed) res = await run();
    }
  } catch (e) {
    // ネットワーク到達不可・CORS拒否など（fetch自体が例外）
    throw new Error(`net:${e instanceof Error ? e.message : String(e)}`);
  }
  if (!res.ok) throw new Error(`http:${res.status}`);
  return URL.createObjectURL(await res.blob());
}

/**
 * 自分のアイコン画像を送る（blob=null で削除）。FormData を送るので apiFetch（JSON固定）は使わない。
 * 成功すると新しいファイル名（削除時は null）を返す。
 */
export async function saveMyAvatar(userId: string, blob: Blob | null): Promise<string | null> {
  const run = () => {
    const headers: Record<string, string> = { 'ngrok-skip-browser-warning': '1' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const sub = getWebCompanySubdomain();
    if (sub) headers['X-Company-Subdomain'] = sub;
    const offlineTok = getOfflineToken();
    if (offlineTok) headers['X-Offline-Token'] = offlineTok;
    const deviceTok = getDeviceToken();
    if (deviceTok) headers['X-Device-Token'] = deviceTok;
    const url = `${API_BASE}/users/${encodeURIComponent(userId)}/avatar`;
    if (!blob) return fetch(url, { method: 'DELETE', headers, credentials: 'include' });
    const fd = new FormData();
    fd.append('avatar', blob, 'avatar.jpg');
    return fetch(url, { method: 'POST', body: fd, headers, credentials: 'include' });
  };
  let res: Response;
  try {
    res = await run();
    if (res.status === 401 && await silentRefresh()) res = await run();
  } catch {
    throw new Error('通信できませんでした。電波のある所でもう一度お試しください。');
  }
  const body = await res.json().catch(() => ({})) as { data?: { avatar_path?: string }; error?: string | { message?: string } };
  if (!res.ok) {
    const msg = typeof body.error === 'string' ? body.error : body.error?.message;
    throw new Error(msg ?? `保存できませんでした（${res.status}）`);
  }
  return blob ? (body.data?.avatar_path ?? null) : null;
}

/** このAPKに埋め込まれた会社ID（ビルド時に .env.android で設定） */
export const COMPANY_ID: string = import.meta.env.VITE_COMPANY_ID ?? 'comp-001';

/**
 * PCブラウザからのアクセス時に URL のサブドメインで会社を特定する
 * 例: test-kaisya.crew.unison-sea.com → 'test-kaisya'（個人用ホスト test-kaisya.my.unison-sea.com も同じ）
 * APK / crew.unison-sea.com の場合は null を返し COMPANY_ID を使う
 */
export function getWebCompanySubdomain(): string | null {
  try {
    const parts = window.location.hostname.split('.');
    // test-kaisya.crew.unison-sea.com → ['test-kaisya','crew','unison-sea','com']
    if (parts.length >= 4 && (parts[1] === 'crew' || parts[1] === 'my')) return parts[0];
  } catch { /* Capacitor ネイティブ環境など */ }
  return null;
}

/** 個人用アプリ専用のホスト（<会社>.my.unison-sea.com）で開いているか */
function isPersonalHost(): boolean {
  try {
    const parts = window.location.hostname.split('.');
    return parts.length >= 4 && parts[1] === 'my';
  } catch { return false; }
}

// ── 会社ブランディング（ロゴ・テーマ色。未ログインでも取得可） ──────────
export interface Branding { name: string; logo_url: string | null; primary_color: string | null }

/** キャッシュ済みブランディング（オフライン時・初期描画用） */
export function getCachedBranding(): Branding | null {
  try { return JSON.parse(localStorage.getItem('catms_branding') ?? 'null'); } catch { return null; }
}

/** 会社ロゴ等を取得して端末にキャッシュする（失敗時は null） */
export async function fetchBranding(): Promise<Branding | null> {
  try {
    const sub = getWebCompanySubdomain();
    const q = sub ? `subdomain=${encodeURIComponent(sub)}` : `company_id=${encodeURIComponent(COMPANY_ID)}`;
    const res = await fetch(`${API_BASE}/branding?${q}`);
    if (!res.ok) return null;
    const json = await res.json();
    const b: Branding | null = json?.data ?? null;
    if (b) localStorage.setItem('catms_branding', JSON.stringify(b));
    return b;
  } catch { return null; }
}

/** Auth - Login */
export async function login(identifier: string, password: string) {
  try {
    const res = await apiFetch<{ success: boolean; data: {
      access_token: string; refresh_token: string; expires_in: number; user: Record<string, unknown>;
      offline?: { secret?: string; password_hash?: string; face_descriptor?: number[] | null };
    } }>('/auth/login', {
      method: 'POST',
      // company_id を送信してバックエンドがこの会社の中からユーザーを検索する
      // Web版はX-Company-Subdomainヘッダで会社特定済みのため company_id 不要
      body: JSON.stringify({ employee_code: identifier, password, ...(!getWebCompanySubdomain() && { company_id: COMPANY_ID }) }),
    });
    // オフライン認証パックを端末にキャッシュ（次回オフラインのパスワードログイン・同期用）
    try {
      const d = res.data;
      if (d.offline && d.user) {
        saveOfflineBundle({
          user_id: String(d.user["user_id"] ?? d.user["id"] ?? ''),
          employee_code: String(d.user["employee_code"] ?? identifier),
          user_name: String(d.user["user_name"] ?? ''),
          user_role: String(d.user["user_role"] ?? 'crew'),
          company_id: String(d.user["company_id"] ?? ''),
          avatar_path: (d.user["avatar_path"] as string | null | undefined) ?? null,
          password_hash: d.offline.password_hash,
          secret: d.offline.secret,
          face_descriptor: d.offline.face_descriptor ?? null,
        });
      }
    } catch { /* キャッシュ失敗は無視 */ }
    return res.data;
  } catch (e) {
    // ネットワーク系はそのまま投げてオフラインログインへフォールバックさせる
    const msg = e instanceof Error ? e.message : '';
    if (e instanceof TypeError || /fetch|Failed to fetch|NetworkError|ネットワーク|接続|timeout/i.test(msg)) throw e;
    // サーバーが理由を返しているならそのまま見せる。
    // 以前はここで一律「IDまたはパスワードが正しくありません」に書き換えており、
    // 回数制限（429）でも「パスワードが違う」と表示されて原因が分からなくなっていた。
    throw new Error(msg && !/^HTTP \d+$/.test(msg) ? msg : 'IDまたはパスワードが正しくありません。');
  }
}

/** 顔認証: 登録時に特徴量をサーバ保存し、オフライン用シークレットを取得（要認証） */
export async function faceRegisterSecret(descriptor?: number[]): Promise<string> {
  const res = await apiFetch<{ success: boolean; data: { secret: string } }>('/auth/face-register', {
    method: 'POST',
    body: JSON.stringify({ descriptor: descriptor ?? [] }),
  });
  return res.data.secret;
}

export interface RosterEntry {
  user_id: string; employee_code: string; user_name: string; user_role: string;
  company_id: string; short_id?: string; avatar_path?: string | null;
  password_hash?: string; secret?: string;
  face_descriptor?: number[] | null;
}

/**
 * 会社全乗員のオフライン情報を一括取得して端末にキャッシュする（共有端末向け）。
 * オンライン時／復帰時に呼ぶことで、オフライン中でも任意の乗員へ切り替えてログインできる。
 * 顔ストアへの反映は呼び出し側（App）が返り値を使って行う。
 */
/** 端末に登録された船舶と、ログイン船員の配置船舶が一致するか検証（不一致ならログインを止める） */
export async function verifyBoarding(): Promise<{ match: boolean; deviceVesselName?: string; assignedVesselName?: string }> {
  const deviceToken = localStorage.getItem('catms_device_token');
  if (!deviceToken) return { match: true };
  try {
    const res = await apiFetch<{ success: boolean; data: { match: boolean; deviceVesselName?: string; assignedVesselName?: string } }>(
      '/devices/verify-boarding',
      { method: 'POST', body: JSON.stringify({ device_token: deviceToken }) },
      { noRedirect: true },
    );
    return res.data;
  } catch {
    // 検証できない（オフライン等）ときはログインを阻害しない
    return { match: true };
  }
}

export async function fetchOfflineRoster(): Promise<RosterEntry[]> {
  const res = await apiFetch<{ success: boolean; data: RosterEntry[] }>('/auth/offline-roster', { method: 'POST' });
  const entries = res.data ?? [];
  for (const e of entries) {
    saveOfflineBundle({
      user_id: e.user_id, employee_code: e.employee_code, user_name: e.user_name,
      user_role: e.user_role, company_id: e.company_id, avatar_path: e.avatar_path ?? null,
      password_hash: e.password_hash, secret: e.secret, face_descriptor: e.face_descriptor ?? null,
    });
  }
  return entries;
}

/** オフライン認証パックの再取得（オンライン時。パスワード変更の自動反映用） */
export async function refreshOfflineBundle(userId: string, secret: string): Promise<void> {
  const subdomain = getWebCompanySubdomain();
  const res = await fetch(`${API_BASE}/auth/offline-bundle`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
      ...(subdomain ? { 'X-Company-Subdomain': subdomain } : {}),
      ...(getOfflineToken() ? { 'X-Offline-Token': getOfflineToken()! } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ user_id: userId, secret, ...(!subdomain && { company_id: COMPANY_ID }) }),
  });
  if (!res.ok) return; // 失敗時は既存キャッシュを維持
  const json = await res.json() as { data?: { employee_code?: string; user_name?: string; user_role?: string; company_id?: string; avatar_path?: string | null; password_hash?: string; face_descriptor?: number[] | null } };
  const d = json.data;
  if (d) {
    saveOfflineBundle({
      user_id: userId,
      employee_code: d.employee_code ?? '',
      user_name: d.user_name ?? '',
      user_role: d.user_role ?? 'crew',
      company_id: d.company_id ?? '',
      avatar_path: d.avatar_path ?? null,
      password_hash: d.password_hash,
      secret,
      face_descriptor: d.face_descriptor ?? null,
    });
  }
}

/** 顔認証ログイン: 端末で照合した user_id と登録時 secret で本トークンを取得 */
export async function faceLogin(userId: string, secret: string): Promise<{ access_token: string; refresh_token: string; user: Record<string, unknown> }> {
  const subdomain = getWebCompanySubdomain();
  const res = await fetch(`${API_BASE}/auth/face-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
      ...(subdomain ? { 'X-Company-Subdomain': subdomain } : {}),
      ...(getOfflineToken() ? { 'X-Offline-Token': getOfflineToken()! } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ user_id: userId, secret, ...(!subdomain && { company_id: COMPANY_ID }) }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: { message: res.statusText } }));
    throw new Error(e?.error?.message ?? `HTTP ${res.status}`);
  }
  const json = await res.json() as { data: { access_token: string; refresh_token: string; user: Record<string, unknown> } };
  return json.data;
}

/** 指定トークンで打刻を送信（オフラインキュー同期時、本人のトークンで正しく帰属させる） */
export async function postTimestampWithToken(token: string, body: Record<string, unknown>): Promise<void> {
  const subdomain = getWebCompanySubdomain();
  const res = await fetch(`${API_BASE}/timestamps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
      'Authorization': `Bearer ${token}`,
      ...(subdomain ? { 'X-Company-Subdomain': subdomain } : {}),
      // 打刻は管理端末のみ受理されるため、端末識別トークンを必ず添付する
      ...(getOfflineToken() ? { 'X-Offline-Token': getOfflineToken()! } : {}),
      ...(getDeviceToken() ? { 'X-Device-Token': getDeviceToken()! } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({ error: { message: `HTTP ${res.status}` } }));
    throw new Error(e?.error?.message ?? `HTTP ${res.status}`);
  }
}

/** Timestamp - Create */
export async function createTimestamp(data: Record<string, unknown>) {
  const res = await apiFetch<{ data: Record<string, unknown> }>('/timestamps', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.data;
}

/** Timestamp - Sync offline data */
export async function syncTimestamps(deviceId: string, timestamps: Record<string, unknown>[], communicationType?: string) {
  const res = await apiFetch<{ data: Record<string, unknown> }>('/timestamps/sync', {
    method: 'POST',
    body: JSON.stringify({ device_id: deviceId, timestamps, communication_type: communicationType }),
  });
  return res.data;
}

/** Work Sessions - List */
export async function listWorkSessions(params: Record<string, string> = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch<{ data: Record<string, unknown>[]; meta: Record<string, number> }>(`/work-sessions?${qs}`);
  return res;
}

/** Messages - List */
export async function listMessages(params: Record<string, string> = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch<{ data: Record<string, unknown>[]; meta: Record<string, number> }>(`/messages?${qs}`);
  return res;
}

/** Messages - Send */
export async function sendMessage(receiverUserId: string, messageBody: string, vesselId?: string) {
  const res = await apiFetch<{ data: Record<string, unknown> }>('/messages', {
    method: 'POST',
    body: JSON.stringify({ receiver_user_id: receiverUserId, message_body: messageBody, vessel_id: vesselId }),
  });
  return res.data;
}

/** Alerts - List */
export async function listAlerts(params: Record<string, string> = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch<{ data: Record<string, unknown>[]; meta: Record<string, number> }>(`/alerts?${qs}`);
  return res;
}

/** Alcohol Check - SSE経由でAN-S126aの測定結果を取得 */
export async function startAlcoholCheck(
  onWarming:  (countdown: number) => void,
  onWaiting:  () => void,
  onDone:     (value: number) => void,
  onTimeout:  () => void,
  onError:    (e: Event) => void,
  onNoBreath?: () => void,
): Promise<EventSource> {
  // JWTをURLに露出させないよう、短命チケットを取得する。
  // チケット取得失敗時はURLへのtoken付与（セキュリティリスク）を行わず再試行する。
  let ticketParam = '';
  let ticketFetched = false;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      if (attempt > 0) await new Promise(r => setTimeout(r, 1000));
      // 任意機能のため、401でも打刻フローを中断してログイン画面へ飛ばさない（noRedirect）。
      const res = await apiFetch<{ data: { ticket: string } }>('/alcohol/sse-ticket', { method: 'POST' }, { noRedirect: true });
      ticketParam = `ticket=${res.data.ticket}`;
      ticketFetched = true;
      break;
    } catch {
      // 次のループで再試行
    }
  }
  if (!ticketFetched) {
    // 両試行とも失敗 → JWT をURLに露出させないままエラーをスロー
    throw new Error('SSE チケットの取得に失敗しました。接続を確認してください。');
  }
  const url = `${API_BASE}/alcohol/check?${ticketParam}&ngrok-skip-browser-warning=1`;
  const es = new EventSource(url);
  es.onmessage = (e) => {
    const data = JSON.parse(e.data);
    if (data.status === "warming")   onWarming(data.countdown ?? 0);
    if (data.status === "waiting")   onWaiting();
    if (data.status === "done")      { onDone(data.alcohol_value); es.close(); }
    if (data.status === "timeout")   { onTimeout(); es.close(); }
    if (data.status === "no_breath") { (onNoBreath ?? onTimeout)(); es.close(); }
  };
  es.onerror = (e) => { onError(e); es.close(); };
  return es;
}
